import { useMetrics } from "@/hooks/useMetrics";
import type { MetricsFilters } from "@/services/documentsApi";
import { MetricCard } from "./MetricCard";

interface MetricsDashboardProps {
  /** Additional CSS classes */
  className?: string;
  /** Optional date filters for completed/review counts (in_process is always unfiltered) */
  filters?: MetricsFilters;
}

/**
 * MetricsDashboard component - Displays document metrics in a grid
 *
 * Shows three metric cards:
 * - Total Complete (green) - Documents with COMPLETE status (filtered by date)
 * - In Process (blue) - Documents with PENDING, UPLOADED, or PROCESSING status (NEVER filtered by date)
 * - Requiring Review (red) - Documents with REQUIRES_REVIEW status (filtered by date)
 *
 * Note: "In Process" is intentionally NOT filtered by date - users always need to see
 * their total queue depth regardless of the date filter they're viewing.
 *
 * Auto-refreshes every 10 seconds using the useMetrics hook.
 */
export function MetricsDashboard({ className = "", filters }: MetricsDashboardProps) {
  const { data: metrics, isLoading, error } = useMetrics({ filters });

  // If there's an error, show a simple error state
  if (error) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
        <div className="col-span-full bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400 text-sm">
            Failed to load metrics. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
      {/* Total Complete */}
      <MetricCard
        title="Total Complete"
        value={metrics?.total_completed ?? 0}
        icon="task_alt"
        iconBgColor="bg-green-100 dark:bg-green-900/30"
        iconColor="text-green-600 dark:text-green-400"
        isLoading={isLoading}
      />

      {/* In Process */}
      <MetricCard
        title="In Process"
        value={metrics?.in_process ?? 0}
        icon="sync"
        iconBgColor="bg-blue-100 dark:bg-blue-900/30"
        iconColor="text-blue-600 dark:text-blue-400"
        isLoading={isLoading}
      />

      {/* Requiring Review */}
      <MetricCard
        title="Requiring Review"
        value={metrics?.requires_review ?? 0}
        icon="pending_actions"
        iconBgColor="bg-red-100 dark:bg-red-900/30"
        iconColor="text-red-600 dark:text-red-400"
        isLoading={isLoading}
      />
    </div>
  );
}
